import { Box, Typography, Alert, Button } from '@mui/material'
import { warningBoxSx, primaryButtonSx, outlinedButtonSx } from '../styles'
import { COLORS } from '../theme'

export interface DuplicateState {
  isDuplicate: boolean
  orgName: string | null
  orgUuid: string | null
  hasSubOrgs: boolean
  requestSent: boolean
  emailSent: boolean | null
  // 'charities_number' matches are definitive (same real-world org); 'name'
  // matches are a coincidental clash the applicant may not be part of.
  matchedBy: 'name' | 'charities_number'
  // The colliding org is one the caller already belongs to (only ever set on
  // the save-time path — the proactive check deliberately excludes these).
  // "Request to join" is meaningless here, so this gets its own branch.
  alreadyMember: boolean
  // Umbrella-org message has been explicitly acknowledged. Only meaningful
  // where the caller gates advance on it (see onAcknowledgeUmbrella below).
  acknowledged: boolean
}

export const INITIAL_DUPLICATE: DuplicateState = {
  isDuplicate: false,
  orgName: null,
  orgUuid: null,
  hasSubOrgs: false,
  requestSent: false,
  emailSent: null,
  matchedBy: 'name',
  alreadyMember: false,
  acknowledged: false,
}

export function DuplicateWarning({
  duplicate,
  requesting,
  onRequestJoin,
  onAcknowledgeUmbrella,
  onGoToOrg,
  switchingOrg,
  goToOrgError,
}: {
  duplicate: DuplicateState
  requesting: boolean
  onRequestJoin: () => void
  // When provided, the umbrella message renders a Continue action instead of
  // being purely informational — for callers that block advance until it's
  // acknowledged (see OrgDetails.tsx).
  onAcknowledgeUmbrella?: () => void
  // Switches the caller's selected org to duplicate.orgUuid and navigates
  // there. Only relevant to the alreadyMember branch — omitted entirely
  // (no button rendered) if a caller can't reach that branch.
  onGoToOrg?: (orgUuid: string) => void
  // Disables the Go-to-org button while the switch is in flight.
  switchingOrg?: boolean
  // Shown under the Go-to-org button when the switch fails, so a failure
  // doesn't pass silently.
  goToOrgError?: string | null
}) {
  if (!duplicate.isDuplicate) return null

  // After request sent — show outcome
  if (duplicate.requestSent) {
    if (duplicate.emailSent) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          We've contacted the organisation admin for{' '}
          <strong>{duplicate.orgName}</strong>. You'll receive an email when
          they respond to your request.
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: COLORS.secondary }}>
              If you don't hear back, contact your org admin directly, or reach
              out to{' '}
              <a
                href="mailto:help@fundsorter.com"
                style={{ color: COLORS.link }}
              >
                help@fundsorter.com
              </a>{' '}
              for support.
            </Typography>
          </Box>
        </Alert>
      )
    }
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        We couldn't reach the organisation admin. Please contact them directly,
        or email{' '}
        <a href="mailto:help@fundsorter.com" style={{ color: COLORS.link }}>
          help@fundsorter.com
        </a>{' '}
        for assistance.
      </Alert>
    )
  }

  // Already a member of the colliding org — Request to Join would attach
  // the caller to an org they're already in, so point them there instead.
  if (duplicate.alreadyMember) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        You&apos;re already a member of <strong>{duplicate.orgName}</strong>.
        {onGoToOrg && duplicate.orgUuid && (
          <Box sx={{ mt: 1 }}>
            <Button
              onClick={() => onGoToOrg(duplicate.orgUuid!)}
              variant="outlined"
              size="small"
              disabled={switchingOrg}
              sx={outlinedButtonSx}
            >
              {switchingOrg ? 'Switching...' : `Go to ${duplicate.orgName}`}
            </Button>
            {goToOrgError && (
              <Typography
                role="alert"
                variant="body2"
                sx={{ color: 'error.main', mt: 1 }}
              >
                {goToOrgError}
              </Typography>
            )}
          </Box>
        )}
      </Alert>
    )
  }

  // Umbrella org. Where the caller gates advance on acknowledgement, show a
  // Continue action instead of letting the message go unseen; otherwise
  // (e.g. OrgNumber, which doesn't block this case) stay purely informational.
  if (duplicate.hasSubOrgs) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>{duplicate.orgName}</strong> is already on Fundsorter. This is
        an umbrella organisation — you can create a sub-organisation.
        {onAcknowledgeUmbrella && !duplicate.acknowledged && (
          <Box sx={{ mt: 1 }}>
            <Button
              onClick={onAcknowledgeUmbrella}
              variant="outlined"
              size="small"
              sx={outlinedButtonSx}
            >
              Continue
            </Button>
          </Box>
        )}
      </Alert>
    )
  }

  // A name-only match is a coincidental clash between two possibly-unrelated
  // orgs (orgs.name is unique, so a genuine coincidence has nowhere else to
  // go). Lead with picking a different name; keep join as the secondary path
  // for when it turns out to actually be the same organisation.
  if (duplicate.matchedBy === 'name') {
    return (
      <Box role="alert" sx={{ ...warningBoxSx, mt: 2 }}>
        <Typography sx={{ fontWeight: 600, color: COLORS.primary, mb: 1 }}>
          An organisation named <strong>{duplicate.orgName}</strong> already
          exists on Fundsorter.
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.bodyText, mb: 2 }}>
          If this is a different organisation, please use a more specific
          name so the two aren&apos;t confused. If it&apos;s actually the same
          organisation, you can request to join it instead.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={requesting}
          onClick={onRequestJoin}
          sx={outlinedButtonSx}
        >
          {requesting ? 'Sending...' : `Request to join ${duplicate.orgName} instead`}
        </Button>
        <Typography variant="body2" sx={{ color: COLORS.secondary, mt: 1 }}>
          Need help either way? Contact{' '}
          <a href="mailto:help@fundsorter.com" style={{ color: COLORS.link }}>
            help@fundsorter.com
          </a>
          .
        </Typography>
      </Box>
    )
  }

  // charities_number match — definitively the same real-world org, so hard
  // block and lead with the only sensible action.
  return (
    <Box role="alert" sx={{ ...warningBoxSx, mt: 2 }}>
      <Typography sx={{ fontWeight: 600, color: COLORS.primary, mb: 1 }}>
        This organisation is already a Fundsorter user.
      </Typography>
      <Typography variant="body2" sx={{ color: COLORS.bodyText, mb: 2 }}>
        Please reach out to your organisation admin (who created the account)
        and ask them for an invitation, or click the button below.
      </Typography>
      <Button
        variant="contained"
        size="small"
        disabled={requesting}
        onClick={onRequestJoin}
        sx={primaryButtonSx}
      >
        {requesting ? 'Sending...' : `Request to join ${duplicate.orgName}`}
      </Button>
    </Box>
  )
}
